import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import ModuleExamplesBar from '@/components/ModuleExamplesBar';
import UpdateCuratedExampleButton from '@/components/UpdateCuratedExampleButton';
import ReportGlossary from '@/components/ReportGlossary';
import { useSortFilterBar } from '@/components/SortFilterBar';
import { useCuratedExamples } from '@/hooks/useCuratedExamples';
import { useApplySpeciesDefaults } from '@/hooks/useSpeciesDefaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, TrendingUp, Calendar, Download, Table, Search, Info, Save, FolderOpen, Trash2, Pencil, Check, X as XIcon, Lock } from 'lucide-react';
import { InlineRename } from '@/components/InlineRename';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceDot } from 'recharts';
import { COMING_SOON_SPECIES } from '@/config/species';
import { validateConfigParity } from '@/config/chartExportConfig';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { generateMissingFishIntelligence } from '@/utils/generateIntelligence';
import { MissingFishIntelligenceReport } from '@/components/MissingFishIntelligence';
import WingmanInsight from '@/components/WingmanInsight';
import WingmanNudge, { detectPrognosisPatterns } from '@/components/WingmanNudge';
import FCRVersionGuidanceNote from '@/components/FCRVersionGuidanceNote';
import { formatApiErrorDetail } from '@/utils/formatApiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GrowthPrognosis() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { formatCostLabel } = useCredits();
  // Read-only mode: user not approved (pending admin approval)
  const isReadOnly = !user?.is_approved && !user?.is_unlimited && !user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState([]); // Store multiple scenarios
  const [temperatureRegions, setTemperatureRegions] = useState([]);
  const [selectedScenarioForTable, setSelectedScenarioForTable] = useState(null); // legacy — kept for save/load compat
  const [showTable, setShowTable] = useState(false);

  // Sample Checkpoints for Missing Fish Analysis
  const [sampleCheckpoints, setSampleCheckpoints] = useState([
    { date: '', actual_weight_g: '' }
  ]);
  const [missingFishIntelligence, setMissingFishIntelligence] = useState(null);
  const [showMissingFishIntelligence, setShowMissingFishIntelligence] = useState(false);
  const [trajectoryJump, setTrajectoryJump] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfExportRef = useRef(null);
  const mfiPdfRef = useRef(null);
  const formRef = useRef(null);
  // Set to true by `loadExampleAndRun` and consumed inside `handleSubmit`
  // so curated "Try [Species]" runs POST with `?example=1` (quota-free).
  const exampleRunRef = useRef(false);
  // Tracks the curated example just loaded so the admin "Update Try-X" CTA
  // can render. Cleared whenever a different (non-curated) record loads.
  const [loadedCurated, setLoadedCurated] = useState(null);

  // Admin-curated Growth Prognosis "Try [Species]" examples (replaces
  // GROWTH_PROGNOSIS_EXAMPLES from the old moduleExamples.js).
  const { examples: prognosisExamples, refetch: refetchPrognosisExamples } = useCuratedExamples('growth_prognosis');

  // Try-with-Example handler — autofills inputs (and Tilapia monthly temps
  // when relevant) and triggers the run on the next tick.
  //
  // Robust against malformed presets: empty-string / undefined values
  // would clobber the form's built-in defaults (e.g. cycle_mortality_percent
  // defaults to '10') and then the numeric guard at submit time would fail
  // with "cycle mortality percent is required". We filter those out before
  // spreading so a partial preset never resets a valid default to blank.
  // Initial form shape — single source of truth for the Growth Prognosis
  // form defaults. Referenced by `loadExampleAndRun` so switching between
  // Try-X examples never leaves stale fields from the previous species on
  // the form (e.g. Seabream's `genetic_origin=Greek` leaking into a Try-
  // Tilapia run where genetic_origin is empty).
  const FORM_DEFAULTS = {
    species: 'Seabream',
    genetic_origin: 'Greek',
    fcr_version_id: '',
    initial_average_weight_g: '',
    initial_date: '',
    temperature_region: '',
    target_average_weight_g: '',
    initial_fish_number: '',
    cycle_mortality_percent: '10',
    model_adjustment_percent: '100',
    daily_feed_adjustment_percent: '100',
    model_source: 'TPS',
    custom_model_id: '',
  };

  const loadExampleAndRun = (preset) => {
    if (isReadOnly) return;
    const rawInputs = preset?.inputs || {};
    // Fresh slate: start from the form's initial defaults and overlay the
    // preset's fields. Spreading `prev` here (the old code path) caused
    // seepage when the new preset didn't carry a key that the previous
    // preset did — e.g. Tilapia omits `genetic_origin`, so the prior
    // Seabream's "Greek" would stick.
    const nextFormData = { ...FORM_DEFAULTS, ...rawInputs };
    setFormData(nextFormData);
    if (preset.monthly_temperatures) {
      setMonthlyTemperatures((prev) => ({ ...prev, ...preset.monthly_temperatures }));
    }
    toast.success(`Loaded ${preset.species_label} example — running prognosis…`);
    // Flag this submit as a curated example run so the POST goes out with
    // `?example=1` and the backend skips credit billing.
    exampleRunRef.current = true;
    if (preset?.source_record_id) {
      setCurrentRecordId(preset.source_record_id);
      setLoadedCurated({
        module: 'growth_prognosis',
        species: preset.species_label,
        source_record_id: preset.source_record_id,
        current_name: preset.source_record_name || `Try-${preset.species_label} Example`,
      });
    }
    // Double-tick: re-apply the preset after the species-defaults hook has
    // had a chance to run (its `onApply` patch fires on species change and
    // would otherwise clobber `temperature_region`/`genetic_origin` from
    // the preset), then submit. Same pattern used in GrowthModelAnalysis.
    setTimeout(() => {
      setFormData((prev) => ({ ...prev, ...rawInputs }));
      setTimeout(() => { formRef.current?.requestSubmit?.(); }, 60);
    }, 80);
  };

  // Save/Load Records State
  const [savedRecords, setSavedRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [recordName, setRecordName] = useState('');
  const [currentRecordId, setCurrentRecordId] = useState(null);

  // Excel-style sort + multi-select filter over saved prognoses.
  const prognosisRecordColumns = useMemo(() => ([
    { key: 'name',          label: 'Name',          accessor: r => r.name,                            type: 'text' },
    { key: 'species',       label: 'Species',       accessor: r => r.species,                         type: 'text' },
    { key: 'genetic',       label: 'Genetic',       accessor: r => r.genetic_origin,                  type: 'text' },
    { key: 'init_w',        label: 'Initial (g)',   accessor: r => r.initial_average_weight_g,        type: 'number' },
    { key: 'target_w',      label: 'Target (g)',    accessor: r => r.target_average_weight_g,         type: 'number' },
    { key: 'scenarios',     label: '# Scenarios',   accessor: r => (Array.isArray(r.scenarios) ? r.scenarios.length : 0), type: 'number' },
    { key: 'updated_at',    label: 'Updated',       accessor: r => r.updated_at,                      type: 'date' },
  ]), []);
  const { processedRecords: filteredPrognosisRecords, bar: prognosisSortFilterBar } = useSortFilterBar(
    savedRecords,
    prognosisRecordColumns,
    'prognosis-records',
  );

  const [formData, setFormData] = useState({
    species: 'Seabream',
    genetic_origin: 'Greek',
    fcr_version_id: '',
    initial_average_weight_g: '',
    initial_date: '',
    temperature_region: '',
    target_average_weight_g: '',
    initial_fish_number: '',
    cycle_mortality_percent: '10',
    model_adjustment_percent: '100',
    daily_feed_adjustment_percent: '100',
    model_source: 'TPS',  // 'TPS' or 'CUSTOM'
    custom_model_id: '',  // Selected custom model ID
  });

  // Custom model selection state
  const [approvedModels, setApprovedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [userSpecies, setUserSpecies] = useState([]);

  // FCR Origins state (for the FCR Version selector) — mirrors the implementation
  // in GrowthModelAnalysis.js / SnapshotAnalysis.js so users can pick a multiplier
  // version that matches their cage's historical FCR performance.
  const [fcrOrigins, setFcrOrigins] = useState([]);

  useEffect(() => {
    const fetchFcrOrigins = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/fcr-origins`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setFcrOrigins(resp.data?.origins || []);
      } catch { /* non-fatal */ }
    };
    if (token) fetchFcrOrigins();
  }, [token]);

  // Monthly temperatures for Tilapia (12 months)
  const [monthlyTemperatures, setMonthlyTemperatures] = useState({
    january: '', february: '', march: '', april: '',
    may: '', june: '', july: '', august: '',
    september: '', october: '', november: '', december: ''
  });
  const [temperaturePreview, setTemperaturePreview] = useState(null);

  // Auto-populate species defaults (genetic origin, FCR version, temp region,
  // model source) when species changes — platform-wide single source. MUST
  // be declared AFTER the formData/monthlyTemperatures useState calls
  // (TDZ regression — see iteration_215.json).
  useApplySpeciesDefaults({
    token,
    species: formData.species,
    keys: ['genetic_origin', 'fcr_version_id', 'temperature_region', 'model_source'],
    onApply: (patch) => setFormData((prev) => ({ ...prev, ...patch })),
  });

  // Fetch temperature regions on mount
  useEffect(() => {
    const fetchTemperatureRegions = async () => {
      try {
        const authToken = token; // from useAuth
        // First try to fetch with custom models (requires auth)
        if (token) {
          try {
            const response = await axios.get(
              `${BACKEND_URL}/api/calculator/temperature-regions-with-custom`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            // Combine standard regions, custom models, and public models
            const standardRegions = response.data.standard_regions || [];
            const customModels = response.data.custom_models || [];
            const publicModels = response.data.public_models || [];
            
            // Format: standard regions first, then user's custom models, then public models
            const allRegions = [
              ...standardRegions,
              ...customModels.map(m => ({ 
                id: m.id, 
                name: m.name, 
                isCustom: true,
                displayName: `My Model: ${m.name}`
              })),
              ...publicModels.map(m => ({ 
                id: m.id, 
                name: m.name, 
                isPublic: true,
                displayName: `Shared: ${m.name}`
              }))
            ];
            setTemperatureRegions(allRegions);
            return;
          } catch (err) {
            // Falling back to standard regions
          }
        }
        
        // Fallback: standard regions only
        const response = await axios.get(`${BACKEND_URL}/api/calculator/temperature-regions`);
        setTemperatureRegions(response.data.regions || []);
      } catch (err) {
        console.error('Failed to fetch temperature regions:', err);
        toast.error('Failed to load temperature regions');
      }
    };
    
    fetchTemperatureRegions();
  }, []);

  // Fetch user-specific species
  useEffect(() => {
    const fetchUserSpecies = async () => {
      try {
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/user-species`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserSpecies(resp.data.species || []);
      } catch { /* fallback: only base species shown */ }
    };
    fetchUserSpecies();
  }, []);

  // PRD UX-fix #4: resolve a raw temperature_region value (possibly the
  // internal "custom:UUID" form) to a human-friendly label so we never leak
  // the UUID into PDFs, AI Wingman context, or report copy. Falls back to
  // "custom temperature profile" when the model record is no longer
  // available (e.g. shared profile revoked).
  const resolveTemperatureRegionLabel = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return rawValue || '';
    if (!rawValue.startsWith('custom:')) return rawValue;
    const id = rawValue.slice('custom:'.length);
    const match = (temperatureRegions || []).find(
      (r) => typeof r === 'object' && r && r.id === id
    );
    return (match && (match.displayName || match.name)) || 'Custom temperature profile';
  };

  // Fetch approved custom models and user's model assignment
  useEffect(() => {
    const fetchModelData = async () => {
      setLoadingModels(true);
      try {
        const authToken = token; // from useAuth
        
        // Fetch approved models
        const modelsResponse = await axios.get(
          `${BACKEND_URL}/api/custom-models/approved?model_type=SGR`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setApprovedModels(modelsResponse.data || []);
        
        // Fetch user's model assignment (for pre-selection)
        try {
          const assignmentResponse = await axios.get(
            `${BACKEND_URL}/api/user/model-assignment`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Pre-select based on admin assignment
          if (assignmentResponse.data.sgr_model_source === 'CUSTOM' && assignmentResponse.data.sgr_custom_model_id) {
            setFormData(prev => ({
              ...prev,
              model_source: 'CUSTOM',
              custom_model_id: assignmentResponse.data.sgr_custom_model_id
            }));
          }
        } catch (assignmentErr) {
          // Assignment fetch failed - use defaults
        }
      } catch (err) {
        console.error('Failed to fetch approved models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    
    fetchModelData();
  }, []);

  // Fetch saved prognosis records on page load
  useEffect(() => {
    fetchSavedRecords();
  }, []);

  // Fetch saved prognosis records
  const fetchSavedRecords = async () => {
    setLoadingRecords(true);
    try {
      const authToken = token; // from useAuth
      const response = await axios.get(
        `${BACKEND_URL}/api/prognosis-records`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSavedRecords(response.data || []);
    } catch (err) {
      console.error('Failed to fetch prognosis records:', err);
      toast.error('Failed to load saved records');
    } finally {
      setLoadingRecords(false);
    }
  };

  // Save current prognosis
  const handleSaveRecord = async () => {
    if (!recordName.trim()) {
      toast.error('Please enter a name for this prognosis');
      return;
    }

    if (scenarios.length === 0) {
      toast.error('Please calculate a prognosis before saving');
      return;
    }

    setSavingRecord(true);
    try {
      const authToken = token; // from useAuth
      const recordData = {
        name: recordName,
        species: formData.species,
        genetic_origin: formData.genetic_origin,
        fcr_version_id: formData.fcr_version_id || null,
        temperature_region: formData.temperature_region,
        initial_average_weight_g: parseFloat(formData.initial_average_weight_g),
        initial_date: formData.initial_date,
        target_average_weight_g: parseFloat(formData.target_average_weight_g),
        initial_fish_number: parseInt(formData.initial_fish_number),
        cycle_mortality_percent: parseFloat(formData.cycle_mortality_percent),
        scenarios: scenarios,
        sample_checkpoints: sampleCheckpoints,
        missing_fish_analysis: missingFishIntelligence
      };

      if (currentRecordId) {
        // Update existing record
        await axios.put(
          `${BACKEND_URL}/api/prognosis-records/${currentRecordId}`,
          recordData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Prognosis updated successfully');
      } else {
        // Create new record
        const response = await axios.post(
          `${BACKEND_URL}/api/prognosis-records`,
          recordData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setCurrentRecordId(response.data.id);
        toast.success('Prognosis saved successfully');
      }

      setShowSaveDialog(false);
    } catch (err) {
      console.error('Failed to save prognosis:', err);
      toast.error('Failed to save prognosis');
    } finally {
      setSavingRecord(false);
    }
  };

  // Load a saved prognosis
  const handleLoadRecord = async (record) => {
    setFormData(prev => ({
      ...prev,
      species: record.species,
      genetic_origin: record.genetic_origin,
      fcr_version_id: record.fcr_version_id || '',
      temperature_region: record.temperature_region,
      initial_average_weight_g: String(record.initial_average_weight_g),
      initial_date: record.initial_date,
      target_average_weight_g: String(record.target_average_weight_g),
      initial_fish_number: String(record.initial_fish_number),
      cycle_mortality_percent: String(record.cycle_mortality_percent)
    }));
    setScenarios(record.scenarios || []);
    setSampleCheckpoints(record.sample_checkpoints || [{ date: '', actual_weight_g: '' }]);
    setMissingFishIntelligence(record.missing_fish_analysis || null);
    setShowMissingFishIntelligence(!!record.missing_fish_analysis);
    setCurrentRecordId(record.id);
    setLoadedCurated(null);
    setRecordName(record.name);
    setShowLoadDialog(false);
    toast.success(`Loaded: ${record.name}`);
  };

  // Delete a saved prognosis
  const handleDeleteRecord = async (recordId, recordName) => {
    if (!window.confirm(`Delete "${recordName}"? This cannot be undone.`)) return;

    try {
      const authToken = token; // from useAuth
      await axios.delete(
        `${BACKEND_URL}/api/prognosis-records/${recordId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSavedRecords(prev => prev.filter(r => r.id !== recordId));
      if (currentRecordId === recordId) {
        setCurrentRecordId(null);
        setRecordName('');
      }
      toast.success('Record deleted');
    } catch (err) {
      console.error('Failed to delete record:', err);
      toast.error('Failed to delete record');
    }
  };

  const renamePrognosisRecord = async (recordId, newName) => {
    const authToken = token; // from useAuth
    await axios.patch(
      `${BACKEND_URL}/api/prognosis-records/${recordId}/rename`,
      { name: newName },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    setSavedRecords(prev => prev.map(r => r.id === recordId ? { ...r, name: newName } : r));
    toast.success('Record renamed');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      // Validate Tilapia-specific rules (Phase B: static time-driven model)
      if (formData.species === 'Tilapia') {
        // Static model no longer requires monthly temperatures. It has its
        // own hard limits (20g floor, 1402.45g ceiling / 219-day horizon).
        const initWeight = parseFloat(formData.initial_average_weight_g);
        const targetWeight = parseFloat(formData.target_average_weight_g);
        if (initWeight < 20) {
          throw new Error('For Tilapia, initial weight must be at least 20g (static model floor).');
        }
        if (targetWeight > 1400) {
          throw new Error('For Tilapia, target weight must not exceed 1,400g (static model horizon: day 219, 1,402g).');
        }
      }

      // Validate common inputs
      if (!formData.initial_average_weight_g || !formData.initial_date || 
          !formData.target_average_weight_g || !formData.initial_fish_number) {
        throw new Error('Please fill in all required fields');
      }

      // Validate temperature region for SGR+temperature species
      // (Seabream + Seabass). Tilapia / Tripletail use static time-driven
      // curves so no region is needed.
      if (formData.species !== 'Tilapia' && formData.species !== 'Tripletail' && !formData.temperature_region) {
        throw new Error('Please select a temperature region');
      }

      // Validate genetic origin for Seabream only
      if (formData.species !== 'Tilapia' && formData.species !== 'Seabass' && formData.species !== 'Tripletail' && !formData.genetic_origin) {
        throw new Error('Please select a genetic origin');
      }

      const initialWeight = parseFloat(formData.initial_average_weight_g);
      const targetWeight = parseFloat(formData.target_average_weight_g);

      // Species-specific weight limits (Phase B: Tilapia static model horizon)
      const maxWeight = formData.species === 'Tilapia' ? 1400 : formData.species === 'Seabass' ? 600 : formData.species === 'Tripletail' ? 935 : 800;
      if (targetWeight > maxWeight) {
        throw new Error(`Target weight cannot exceed ${maxWeight}g (${formData.species} model limit)`);
      }

      if (targetWeight <= initialWeight) {
        throw new Error('Target weight must be greater than initial weight');
      }

      // Validate feed adjustment is divisible by 5
      const feedAdjustment = parseFloat(formData.daily_feed_adjustment_percent || 100);
      if (feedAdjustment % 5 !== 0) {
        throw new Error('Feed adjustment must be in increments of 5%');
      }

      const payload = {
        species: formData.species,
        genetic_origin: (formData.species === 'Tilapia' || formData.species === 'Seabass' || formData.species === 'Tripletail') ? null : formData.genetic_origin,
        fcr_version_id: formData.fcr_version_id || null,
        initial_average_weight_g: initialWeight,
        initial_date: formData.initial_date,
        temperature_region: formData.species === 'Tilapia' ? 'Tilapia-Custom' : formData.species === 'Tripletail' ? 'N/A' : formData.temperature_region,
        target_average_weight_g: targetWeight,
        initial_fish_number: parseInt(formData.initial_fish_number),
        cycle_mortality_percent: parseFloat(formData.cycle_mortality_percent),
        model_adjustment_percent: parseFloat(formData.model_adjustment_percent || 100),
        daily_feed_adjustment_percent: feedAdjustment
      };

      // Soft-recover: if the curated "Try …" preset was loaded a hair too
      // quickly for React to commit the Select value, fall back to the
      // form's canonical defaults instead of making the user retry.
      // Same shape as the hardcoded `useState` defaults above.
      if (!Number.isFinite(payload.cycle_mortality_percent)) {
        payload.cycle_mortality_percent = 10;
      }
      if (!Number.isFinite(payload.model_adjustment_percent)) {
        payload.model_adjustment_percent = 100;
      }

      // Defensive guard — fail loudly with a friendly message instead of
      // silently sending `null` for fields whose preset/auto-fill value
      // didn't match a Select option (e.g., a curated example carrying
      // cycle_mortality_percent="8" while the dropdown only offers 5/10/15/20/25).
      // parseFloat('') / parseFloat(undefined) are NaN, which JSON
      // serialises as null and the backend rejects with 422.
      // (Only `initial_fish_number` reaches this guard now — the two % fields
      // are recovered above.)
      const _numericRequired = {
        initial_fish_number: payload.initial_fish_number,
      };
      for (const [k, v] of Object.entries(_numericRequired)) {
        if (!Number.isFinite(v)) {
          throw new Error(
            `${k.replace(/_/g, ' ')} is required — please pick a valid value before running the prognosis.`
          );
        }
      }

      // Phase B: no monthly_temperatures sent for Tilapia — static model is
      // temperature-agnostic for growth; feed uses weight-indexed BFCR.

      const isExampleRun = exampleRunRef.current;
      exampleRunRef.current = false;

      const response = await axios.post(
        `${BACKEND_URL}/api/analysis/growth-prognosis${isExampleRun ? '?example=1' : ''}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Replace existing scenario (not append) so the chart refreshes without duplicates
      const feedLabel = feedAdjustment !== 100 ? `, Feed ${feedAdjustment}%` : '';
      const newScenario = {
        ...response.data,
        scenarioId: Date.now(),
        scenarioLabel: `Prognosis (${response.data.model_adjustment_percent}% Model${feedLabel})`
      };
      
      setScenarios([newScenario]);
      setResult(newScenario);
      setSelectedScenarioForTable(newScenario.scenarioId);
      
      // Clear previous intelligence results so stale data isn't shown
      setMissingFishIntelligence(null);
      setShowMissingFishIntelligence(false);
      setTrajectoryJump(null);
      
      toast.success('Growth prognosis calculated');
      
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err, 'Failed to calculate growth prognosis');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (scenarios.length === 0) {
      toast.error('No scenarios to export');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/analysis/growth-prognosis/export-excel`,
        { scenarios },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `growth_prognosis_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel file downloaded successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export Excel file');
    } finally {
      setExporting(false);
    }
  };

  // Sample Checkpoint handlers
  const handleSampleCheckpointChange = (index, field, value) => {
    const newCheckpoints = [...sampleCheckpoints];
    newCheckpoints[index][field] = value;
    setSampleCheckpoints(newCheckpoints);
  };

  const addSampleCheckpoint = () => {
    if (sampleCheckpoints.length < 6) {
      setSampleCheckpoints([...sampleCheckpoints, { date: '', actual_weight_g: '' }]);
    }
  };

  const removeSampleCheckpoint = (index) => {
    if (sampleCheckpoints.length > 1) {
      setSampleCheckpoints(sampleCheckpoints.filter((_, i) => i !== index));
    }
  };

  // Run Missing Fish Intelligence — uses the same shared engine as Spreadsheet Uploader
  const runMissingFishIntelligenceAnalysis = async () => {
    if (scenarios.length === 0) {
      toast.error('Run a growth prognosis first');
      return;
    }

    const latestScenario = scenarios[scenarios.length - 1];
    if (!latestScenario || !latestScenario.growth_trajectory) {
      toast.error('No trajectory data available');
      return;
    }

    // Filter valid checkpoints
    const validCheckpoints = sampleCheckpoints.filter(cp => 
      cp.date && cp.actual_weight_g && !isNaN(parseFloat(cp.actual_weight_g))
    );

    if (validCheckpoints.length === 0) {
      toast.error('Enter at least one sample checkpoint with date and weight');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // DATA SUFFICIENCY GATE — Minimum 2 samples required for trend analysis
    // ═══════════════════════════════════════════════════════════════════════════
    if (validCheckpoints.length < 2) {
      // Force minimal "insufficient data" intelligence output
      const insufficientDataIntel = {
        // SSOT with forced Normal/Low detection
        ssot: {
          detectionStrength: 'Low',
          detectionStrengthScore: 0,
          detectionStrengthDrivers: ['Insufficient data (minimum 2 samples required)'],
          primaryMethod: null,
          primaryMethodLabel: 'Insufficient Data',
          adjustedFishCount: null,
          originalFishCount: parseInt(formData.initial_fish_number) || 100000,
          missingFishCount: 0,
          missingFishPercent: 0,
          hasMissingFish: false,
          rfi: null,
          feedingState: null,
          excessFeedKg: 0,
          wastedFeedKg: 0,
        },
        // Decision context forced to MONITOR_ONLY
        decisionContext: {
          detection: {
            type: 'Normal',
            strength: 'Low',
            probability: null,
            summary: 'Insufficient data to assess stock discrepancy (minimum 2 samples required)',
          },
          dataReliability: {
            score: 15,
            label: 'Low',
            classification: { label: 'Insufficient evidence', tier: 'insufficient', color: 'red' },
            drivers: [
              { factor: 'Sample count', status: 'low', detail: `Only ${validCheckpoints.length} sample` },
              { factor: 'Trend data', status: 'low', detail: 'Cannot establish trend' },
            ],
            flags: ['insufficient_samples'],
            sensitivityNote: 'Minimum 2 samples required for any trend analysis.',
            warning: 'Insufficient data — no analysis possible.',
          },
          financialContext: {
            hasFeedData: false,
            isDecisionGrade: false,
            uncertaintyLevel: 'unavailable',
            feedWasteRule: 'No analysis — insufficient data.',
          },
          decisionClass: 'MONITOR_ONLY',
          decisionReason: 'Insufficient data (minimum 2 samples required). Add samples before analysis.',
          downgradeReasons: ['Single data point — no trend can be established'],
          downgradeExplanation: 'Reduced confidence due to: single data point — no trend can be established',
          primaryAction: {
            text: 'Add additional sample(s) before analysis.',
            reversibility: 'REVERSIBLE',
            priority: 'PRIMARY',
          },
          secondaryActions: [
            { text: 'Re-sample within 10–14 days.', reversibility: 'REVERSIBLE' },
            { text: 'Do NOT adjust stock or feeding based on current data.', reversibility: 'REVERSIBLE' },
          ],
          stockRecordPolicy: {
            canModifyStockRecords: false,
            message: 'Do NOT adjust stock or feeding based on current data.',
            detail: 'Single data point cannot establish trends. Add at least 2 samples for analysis.',
          },
          validation: {
            required: true,
            triggers: ['Insufficient data (< 2 samples)'],
            examples: ['Add second sample checkpoint', 'Re-sample within 10–14 days'],
          },
          timestamp: new Date().toISOString(),
        },
        // Disable advanced outputs
        trajectoryJump: null,
        fcrBasedDetection: null,
        growthFit: null,
        methodResolution: {
          activeMethod: null,
          jumpAvailable: false,
          fcrAvailable: false,
        },
        sharedMetrics: null,
        dataQuality: {
          score: 15,
          subscores: { sampleFrequency: 10 },
          details: { totalSamples: validCheckpoints.length },
        },
        // Override interpretation
        coreIntelligence: {
          interpretation: 'Single data point — no trend can be established',
        },
        isGrowthModelMode: true,
        insufficientData: true, // Flag for UI to know this is a gated response
      };

      setMissingFishIntelligence(insufficientDataIntel);
      setShowMissingFishIntelligence(true);
      setTrajectoryJump(null);
      toast.info('Minimum 2 sample checkpoints required for trend analysis. Add more samples.');
      return;
    }
    // ═══════════════════════════════════════════════════════════════════════════

    if (validCheckpoints.length < 3) {
      toast.info('Jump analysis requires at least 3 sample checkpoints. Add more checkpoints for missing fish detection.');
    }

    // Build comparisons array from checkpoints + trajectory (same format as Growth Model)
    const initialDate = new Date(formData.initial_date);
    const comparisons = validCheckpoints.map((cp, idx) => {
      const actualWeight = parseFloat(cp.actual_weight_g);
      const cpDate = new Date(cp.date);
      const formattedDate = `${String(cpDate.getDate()).padStart(2, '0')}/${String(cpDate.getMonth() + 1).padStart(2, '0')}/${String(cpDate.getFullYear()).slice(-2)}`;
      
      // Calculate days since arrival for accurate trajectory rate computation
      const daysSinceArrival = Math.round((cpDate - initialDate) / (1000 * 60 * 60 * 24));

      // Find model weight from trajectory
      let modelWeight = null;
      const directMatch = latestScenario.growth_trajectory.find(p => p.date === formattedDate);
      if (directMatch) {
        modelWeight = directMatch.average_weight_g;
      } else {
        // Nearest date match
        const cpTime = cpDate.getTime();
        let closestDiff = Infinity;
        latestScenario.growth_trajectory.forEach(point => {
          const parts = point.date.split('/');
          const pointDate = new Date(2000 + parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          const diff = Math.abs(pointDate.getTime() - cpTime);
          if (diff < closestDiff) {
            closestDiff = diff;
            modelWeight = point.average_weight_g;
          }
        });
      }

      return {
        sample_number: idx + 1,
        actual_weight_g: actualWeight,
        model_weight_g: modelWeight || actualWeight,
        sample_date: cp.date,
        date: cp.date,
        days_since_arrival: daysSinceArrival,
      };
    }).filter(c => c.model_weight_g != null);

    if (comparisons.length === 0) {
      toast.error('Could not match any checkpoints to model trajectory');
      return;
    }

    // Build growthModelResult compatible with the shared engine
    const growthModelResult = {
      comparisons,
      starting_fish_number: parseInt(formData.initial_fish_number) || 100000,
      starting_weight: parseFloat(formData.initial_average_weight_g) || 10,
      daily_feed_data: null, // Prognosis doesn't have actual feed data
    };

    // Fetch weight-based model FCR for FCR-based detection
    let weightBasedModelFCR = null;
    try {
      const lastComp = comparisons[comparisons.length - 1];
      const lastWeight = parseFloat(lastComp.actual_weight_g);
      const authToken = token; // from useAuth
      const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
        params: { species: formData.species, weight_g: lastWeight, genetic_origin: formData.genetic_origin },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      weightBasedModelFCR = resp.data.model_fcr;
    } catch (err) {
      // Model FCR lookup failed — FCR-based detection will be skipped
    }

    const intel = generateMissingFishIntelligence({
      growthModelResult,
      weightBasedModelFCR,
      detectionMethod: 'jump', // Default to jump for prognosis (no actual feed data for FCR)
    });

    if (!intel) {
      toast.error('Unable to generate intelligence from available data');
      return;
    }

    setMissingFishIntelligence(intel);
    setShowMissingFishIntelligence(true);
    if (intel.trajectoryJump) {
      setTrajectoryJump(intel.trajectoryJump);
    }
    toast.success('Missing Fish Intelligence analysis complete');
  };

  // Export to PDF
  const handleExportPdf = async () => {
    if (scenarios.length === 0) {
      toast.error('No scenarios to export');
      return;
    }

    setExportingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // Narrow margins for larger graph display
      let yPos = margin;

      // Title - CENTER ALIGNED
      pdf.setFontSize(18);
      pdf.setTextColor(8, 145, 178);
      const title = 'Growth Prognosis Report';
      const titleWidth = pdf.getTextWidth(title);
      pdf.text(title, (pageWidth - titleWidth) / 2, yPos);
      yPos += 12;

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      const dateText = `Generated: ${new Date().toLocaleString()}`;
      const dateWidth = pdf.getTextWidth(dateText);
      pdf.text(dateText, (pageWidth - dateWidth) / 2, yPos);
      yPos += 10;

      // Scenario summaries with individual KPIs - Consistent formatting for mobile PDF
      const isMobile = window.innerWidth < 768;
      
      scenarios.forEach((scenario, index) => {
        // Check if we need a new page for this scenario
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        // ===== SCENARIO HEADER =====
        pdf.setFillColor(8, 145, 178);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(`Scenario ${index + 1}: ${scenario.scenarioLabel}`, margin + 3, yPos + 5.5);
        yPos += 12;

        // ===== INDIVIDUAL KPIs FOR THIS SCENARIO =====
        const contentWidth = pageWidth - (margin * 2);
        const colWidth = contentWidth / 2;
        
        // KPI data for this scenario
        const kpis = [
          { label: 'Initial Weight', value: `${scenario.initial_average_weight_g}g` },
          { label: 'Target Weight', value: `${scenario.target_average_weight_g}g` },
          { label: 'Days to Target', value: `${scenario.days_to_target} days` },
          { label: 'Growth Duration', value: `${(scenario.days_to_target / 30).toFixed(1)} months` },
          { label: 'Initial Fish Count', value: scenario.initial_fish_number?.toLocaleString() || 'N/A' },
          { label: 'Survivors at Target', value: scenario.survivors_at_target?.toLocaleString() || 'N/A' },
          { label: 'Cycle Mortality', value: `${scenario.cycle_mortality_percent}%` },
          { label: 'Total Feed Required', value: `${scenario.total_feed_required_kg?.toLocaleString() || 'N/A'} kg` },
          { label: 'Final Biomass', value: `${scenario.final_biomass_kg?.toLocaleString() || 'N/A'} kg` },
          { label: 'Target Date', value: scenario.target_date || 'N/A' },
        ];

        // Draw KPIs in 2-column grid
        pdf.setFontSize(8);
        for (let i = 0; i < kpis.length; i += 2) {
          const leftKpi = kpis[i];
          const rightKpi = kpis[i + 1];
          
          // Left column
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(100, 100, 100);
          pdf.text(`${leftKpi.label}:`, margin, yPos);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(55, 65, 81);
          pdf.text(leftKpi.value, margin + 32, yPos);
          
          // Right column
          if (rightKpi) {
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(100, 100, 100);
            pdf.text(`${rightKpi.label}:`, margin + colWidth, yPos);
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(55, 65, 81);
            pdf.text(rightKpi.value, margin + colWidth + 32, yPos);
          }
          
          yPos += 5;
        }
        
        // Interpretation note
        if (scenario.interpretation) {
          yPos += 2;
          pdf.setFontSize(7);
          pdf.setFont(undefined, 'italic');
          pdf.setTextColor(100, 100, 100);
          const interpretationLines = pdf.splitTextToSize(scenario.interpretation, contentWidth - 10);
          interpretationLines.forEach(line => {
            pdf.text(line, margin + 2, yPos);
            yPos += 4;
          });
        }
        
        yPos += 8; // Space between scenarios
      });

      // ===== CHART CAPTURE =====
      // Growth Prognosis PDF: +33% width increase, center-aligned
      // Preserve height and clarity
      
      // Validate config parity before export (dev mode only)
      if (process.env.NODE_ENV === 'development') {
        validateConfigParity();
      }
      
      if (pdfExportRef.current) {
        const element = pdfExportRef.current;
        const isMobileCapture = window.innerWidth < 768;
        
        // Store original dimensions
        const originalStyles = {
          width: element.style.width,
          minWidth: element.style.minWidth,
          maxWidth: element.style.maxWidth,
          position: element.style.position,
          backgroundColor: element.style.backgroundColor,
          overflow: element.style.overflow
        };
        
        // Force consistent width for capture
        // Mobile: use fixed 1200px (same as Spreadsheet page) for reliable capture
        // Desktop: use 1.33x multiplier
        const targetWidth = isMobileCapture ? 1200 : Math.round(element.offsetWidth * 1.33);
        
        element.style.width = `${targetWidth}px`;
        element.style.minWidth = `${targetWidth}px`;
        element.style.maxWidth = `${targetWidth}px`;
        element.style.position = 'relative';
        element.style.backgroundColor = '#ffffff';
        element.style.overflow = 'visible';
        
        // Trigger resize for Recharts re-render
        window.dispatchEvent(new Event('resize'));
        await new Promise(resolve => setTimeout(resolve, 100));
        window.dispatchEvent(new Event('resize'));
        
        // Wait for SVG re-render
        await new Promise(resolve => {
          let frameCount = 0;
          const waitForFrames = () => {
            frameCount++;
            if (frameCount < 15) {
              requestAnimationFrame(waitForFrames);
            } else {
              setTimeout(resolve, 500);
            }
          };
          requestAnimationFrame(waitForFrames);
        });
        
        // Final resize sync
        window.dispatchEvent(new Event('resize'));
        await new Promise(resolve => setTimeout(resolve, 300));
        
        let canvas;
        try {
          // Mobile: scale 2 to avoid memory issues; Desktop: scale 3
          canvas = await html2canvas(element, {
            scale: isMobileCapture ? 2 : 3,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: -window.scrollY,
          });
        } finally {
          // ALWAYS restore original styles
          element.style.width = originalStyles.width;
          element.style.minWidth = originalStyles.minWidth;
          element.style.maxWidth = originalStyles.maxWidth;
          element.style.position = originalStyles.position;
          element.style.backgroundColor = originalStyles.backgroundColor;
          element.style.overflow = originalStyles.overflow;
          window.dispatchEvent(new Event('resize'));
        }
        
        const imgData = canvas.toDataURL(isMobileCapture ? 'image/jpeg' : 'image/png', 0.92);
        const contentWidth = pageWidth - (margin * 2);
        const aspectRatio = canvas.height / canvas.width;
        
        // Calculate dimensions maintaining aspect ratio
        let finalWidth = contentWidth;
        let finalHeight = contentWidth * aspectRatio;
        
        // Cap height but maintain proper aspect ratio
        if (finalHeight > 120) {
          finalHeight = 120;
          finalWidth = finalHeight / aspectRatio;
        }
        
        if (yPos + finalHeight > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
        }
        
        // Center chart horizontally
        const chartX = margin + (contentWidth - finalWidth) / 2;
        
        // Draw border around the chart
        pdf.setDrawColor(100, 100, 100);
        pdf.setLineWidth(0.5);
        pdf.rect(chartX - 1, yPos - 1, finalWidth + 2, finalHeight + 2);
        
        // Add the chart image
        pdf.addImage(imgData, isMobileCapture ? 'JPEG' : 'PNG', chartX, yPos, finalWidth, finalHeight);
        yPos += finalHeight + 15;
      }

      // ===== SNAPSHOT ANALYSIS SECTION =====
      // Add a summary analysis section for the latest scenario
      const latestScenario = scenarios[scenarios.length - 1];
      if (latestScenario) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        // Section header
        pdf.setFillColor(8, 145, 178);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text('Snapshot Analysis - Key Metrics', margin + 4, yPos + 5.5);
        yPos += 12;

        // Metrics grid (2 columns)
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');
        const metricsColWidth = (pageWidth - (margin * 2)) / 2;
        
        const metrics = [
          { label: 'Days to Target', value: `${latestScenario.days_to_target} days` },
          { label: 'Growth Duration', value: `${(latestScenario.days_to_target / 30).toFixed(1)} months` },
          { label: 'Initial Weight', value: `${latestScenario.initial_average_weight_g}g` },
          { label: 'Target Weight', value: `${latestScenario.target_average_weight_g}g` },
          { label: 'Starting Fish', value: `${latestScenario.initial_fish_number?.toLocaleString() || 'N/A'}` },
          { label: 'Survivors at Target', value: `${latestScenario.survivors_at_target?.toLocaleString() || 'N/A'}` },
          { label: 'Total Feed Required', value: `${latestScenario.total_feed_required_kg?.toLocaleString() || 'N/A'} kg` },
          { label: 'Final Biomass', value: `${latestScenario.final_biomass_kg?.toLocaleString() || 'N/A'} kg` },
        ];

        for (let i = 0; i < metrics.length; i += 2) {
          const leftMetric = metrics[i];
          const rightMetric = metrics[i + 1];
          
          // Left column
          pdf.setTextColor(100, 100, 100);
          pdf.text(leftMetric.label + ':', margin, yPos);
          pdf.setTextColor(0, 0, 0);
          pdf.text(leftMetric.value, margin + 35, yPos);
          
          // Right column
          if (rightMetric) {
            pdf.setTextColor(100, 100, 100);
            pdf.text(rightMetric.label + ':', margin + metricsColWidth, yPos);
            pdf.setTextColor(0, 0, 0);
            pdf.text(rightMetric.value, margin + metricsColWidth + 35, yPos);
          }
          
          yPos += 5;
        }
        yPos += 8;
      }

      // ===== SAMPLE POINTS DATA TABLE =====
      // Show entered sample checkpoints if any were provided
      const validCheckpointsForPdf = sampleCheckpoints.filter(cp => 
        cp.date && cp.actual_weight_g && !isNaN(parseFloat(cp.actual_weight_g))
      );
      
      if (validCheckpointsForPdf.length > 0) {
        if (yPos > pageHeight - 50) {
          pdf.addPage();
          yPos = margin;
        }

        // Section header
        pdf.setFillColor(147, 51, 234);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text('Sample Points Data', margin + 4, yPos + 5.5);
        yPos += 12;

        // Table header
        const spTableWidth = pageWidth - (margin * 2);
        const spColWidths = [15, 50, 45, 45, 35];
        const spHeaders = ['#', 'Sample Date', 'Entered Weight (g)', 'Model Weight (g)', 'Deviation'];
        const spRowHeight = 6;
        
        pdf.setFillColor(243, 232, 255);
        pdf.rect(margin, yPos, spTableWidth, spRowHeight, 'F');
        
        pdf.setFontSize(8);
        pdf.setTextColor(88, 28, 135);
        pdf.setFont(undefined, 'bold');
        
        let spXPos = margin + 2;
        for (let i = 0; i < spHeaders.length; i++) {
          pdf.text(spHeaders[i], spXPos, yPos + 4);
          spXPos += spColWidths[i];
        }
        yPos += spRowHeight;

        // Table rows
        pdf.setFont(undefined, 'normal');
        validCheckpointsForPdf.forEach((cp, index) => {
          if (yPos > pageHeight - 15) {
            pdf.addPage();
            yPos = margin;
          }
          
          // Alternate row background
          if (index % 2 === 0) {
            pdf.setFillColor(250, 245, 255);
            pdf.rect(margin, yPos, spTableWidth, spRowHeight, 'F');
          }
          
          spXPos = margin + 2;
          pdf.setTextColor(55, 65, 81);
          
          // Sample number
          pdf.text(`S${index + 1}`, spXPos, yPos + 4);
          spXPos += spColWidths[0];
          
          // Date
          pdf.text(cp.date || 'N/A', spXPos, yPos + 4);
          spXPos += spColWidths[1];
          
          // Entered weight
          const enteredWeight = parseFloat(cp.actual_weight_g);
          pdf.text(enteredWeight ? enteredWeight.toFixed(1) : 'N/A', spXPos, yPos + 4);
          spXPos += spColWidths[2];
          
          // Model weight (from missingFishAnalysis if available)
          const analysisItem = missingFishIntelligence?.deviations?.find(item => item.sample_date === cp.date);
          const modelWeight = analysisItem?.model_weight_g;
          pdf.text(modelWeight ? modelWeight.toFixed(1) : 'N/A', spXPos, yPos + 4);
          spXPos += spColWidths[3];
          
          // Deviation
          const deviation = analysisItem?.deviation_percent;
          if (deviation !== null && deviation !== undefined) {
            if (deviation > 5) {
              pdf.setTextColor(217, 119, 6); // Amber
            } else if (deviation < -5) {
              pdf.setTextColor(37, 99, 235); // Blue
            } else {
              pdf.setTextColor(22, 163, 74); // Green
            }
            pdf.text(`${deviation > 0 ? '+' : ''}${deviation}%`, spXPos, yPos + 4);
          } else {
            pdf.setTextColor(150, 150, 150);
            pdf.text('N/A', spXPos, yPos + 4);
          }
          
          yPos += spRowHeight;
        });
        yPos += 8;
      }

      // Missing Fish Intelligence (if run) — capture each section individually
      if (showMissingFishIntelligence && missingFishIntelligence && mfiPdfRef.current) {
        pdf.addPage();
        yPos = margin;

        // Section header
        pdf.setFillColor(147, 51, 234);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text('Missing Fish Intelligence Report', margin + 4, yPos + 5.5);
        yPos += 12;

        const mfiElement = mfiPdfRef.current;
        const mfiOriginalBg = mfiElement.style.backgroundColor;
        mfiElement.style.backgroundColor = '#ffffff';

        const sections = mfiElement.querySelectorAll(':scope > div, :scope > table');
        const contentWidth = pageWidth - (margin * 2);

        for (const section of sections) {
          if (section.offsetHeight < 5) continue;
          try {
            const sectionCanvas = await html2canvas(section, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
              scrollX: 0,
              scrollY: -window.scrollY,
              width: Math.max(1100, section.offsetWidth),
            });

            const imgData = sectionCanvas.toDataURL('image/png');
            const aspect = sectionCanvas.height / sectionCanvas.width;
            const imgHeight = contentWidth * aspect;

            if (yPos + imgHeight > pageHeight - margin) {
              pdf.addPage();
              yPos = margin;
            }

            pdf.addImage(imgData, 'PNG', margin, yPos, contentWidth, imgHeight);
            yPos += imgHeight + 2;
          } catch (e) {
            // MFI section capture failed silently
          }
        }

        mfiElement.style.backgroundColor = mfiOriginalBg;
      }

      // Save PDF - mobile-safe approach
      const fileName = `growth_prognosis_${new Date().toISOString().split('T')[0]}.pdf`;
      const isMobileSave = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobileSave) {
        const blob = pdf.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        pdf.save(fileName);
      }
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // Get selected scenario data for table
  const getSelectedScenarioData = () => {
    if (!selectedScenarioForTable) return null;
    return scenarios.find(s => s.scenarioId === selectedScenarioForTable);
  };

  // Helper: convert YYYY-MM-DD to DD/MM/YY (trajectory format)
  const toTrajectoryDateFormat = (isoDate) => {
    const d = new Date(isoDate);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  };

  // Helper: parse DD/MM/YY to Date object
  const parseTrajectoryDate = (dateStr) => {
    const parts = dateStr.split('/');
    return new Date(2000 + parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  };

  // Valid sample checkpoints (reactive to user input)
  const validCheckpoints = useMemo(() => {
    return sampleCheckpoints
      .filter(cp => cp.date && cp.actual_weight_g && !isNaN(parseFloat(cp.actual_weight_g)))
      .map(cp => ({
        ...cp,
        dateFormatted: toTrajectoryDateFormat(cp.date),
        weight: parseFloat(cp.actual_weight_g),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [sampleCheckpoints]);

  // Prepare chart data with up to 3 semantic lines:
  //   1. "Original Forecast" — model trajectory (always present)
  //   2. "Sample-Updated Forecast" — actual sample weights + forward projection (when samples exist)
  //   3. "Corrected Forecast" — corrected for missing fish + forward projection (when intelligence confirms fish loss)
  const chartData = useMemo(() => {
    if (scenarios.length === 0) return [];

    const scenario = scenarios[0];
    const trajectory = scenario.growth_trajectory;

    // Build base array from trajectory with Original Forecast
    const dataArray = trajectory.map(point => ({
      date: point.date,
      _day: point.day,
      _sgr: point.daily_sgr_percent,
      'Original Forecast': parseFloat(point.average_weight_g.toFixed(2)),
    }));

    // ── Line 2: Sample-Updated Forecast ──────────────────────────────────
    if (validCheckpoints.length > 0) {
      const lastCp = validCheckpoints[validCheckpoints.length - 1];
      const lastCpDate = new Date(lastCp.date);

      // Find nearest trajectory index for each sample (closest date match)
      const findNearestIdx = (targetDateStr) => {
        const targetDate = new Date(targetDateStr);
        let bestIdx = -1;
        let bestDiff = Infinity;
        dataArray.forEach((pt, idx) => {
          const ptDate = parseTrajectoryDate(pt.date);
          const diff = Math.abs(ptDate.getTime() - targetDate.getTime());
          if (diff < bestDiff) { bestDiff = diff; bestIdx = idx; }
        });
        return bestIdx;
      };

      // Place actual sample weights at their nearest trajectory dates
      validCheckpoints.forEach(cp => {
        const idx = findNearestIdx(cp.date);
        if (idx !== -1) {
          dataArray[idx]['Sample-Updated Forecast'] = cp.weight;
        }
      });

      // Find trajectory index of last checkpoint and project forward
      const lastCpIdx = findNearestIdx(lastCp.date);
      if (lastCpIdx >= 0) {
        let projWeight = lastCp.weight;
        for (let i = lastCpIdx + 1; i < dataArray.length; i++) {
          projWeight = projWeight * (1 + dataArray[i]._sgr / 100);
          dataArray[i]['Sample-Updated Forecast'] = parseFloat(projWeight.toFixed(2));
        }
      }
    }

    // ── Line 3: Corrected Forecast (only when jump confirmed with missing fish) ──
    const tj = missingFishIntelligence?.trajectoryJump;
    if (tj?.jump_event_confirmed && tj.estimated_missing_fish > 0 && validCheckpoints.length > 0) {
      const expectedWeight = tj.expected_weight_at_jump_from_prior_trajectory;
      const jumpSampleDate = tj.jump_sample_date || tj.probable_loss_window_end;

      if (expectedWeight && jumpSampleDate) {
        const jumpDateObj = new Date(jumpSampleDate);

        // Find nearest trajectory index for the jump date
        let jumpIdx = -1;
        let jumpBestDiff = Infinity;
        dataArray.forEach((pt, idx) => {
          const ptDate = parseTrajectoryDate(pt.date);
          const diff = Math.abs(ptDate.getTime() - jumpDateObj.getTime());
          if (diff < jumpBestDiff) { jumpBestDiff = diff; jumpIdx = idx; }
        });

        // Pre-jump samples: use actual sample weights (same as Sample-Updated)
        validCheckpoints.forEach(cp => {
          const cpDateObj = new Date(cp.date);
          if (cpDateObj < jumpDateObj) {
            let bestIdx = -1;
            let bestDiff = Infinity;
            dataArray.forEach((pt, idx) => {
              const ptDate = parseTrajectoryDate(pt.date);
              const diff = Math.abs(ptDate.getTime() - cpDateObj.getTime());
              if (diff < bestDiff) { bestDiff = diff; bestIdx = idx; }
            });
            if (bestIdx !== -1) {
              dataArray[bestIdx]['Corrected Forecast'] = cp.weight;
            }
          }
        });

        // At jump and forward: use expected weight and project with SGR
        if (jumpIdx !== -1) {
          dataArray[jumpIdx]['Corrected Forecast'] = parseFloat(expectedWeight.toFixed(2));
          let projWeight = expectedWeight;
          for (let i = jumpIdx + 1; i < dataArray.length; i++) {
            projWeight = projWeight * (1 + dataArray[i]._sgr / 100);
            dataArray[i]['Corrected Forecast'] = parseFloat(projWeight.toFixed(2));
          }
        }
      }
    }

    // Remove internal helper fields before returning
    return dataArray.map(({ _day, _sgr, ...rest }) => rest);
  }, [scenarios, validCheckpoints, missingFishIntelligence]);

  // ── Sample-Updated Scenario: full revised trajectory with KPIs ────────
  const sampleUpdatedScenario = useMemo(() => {
    if (scenarios.length === 0 || validCheckpoints.length === 0) return null;

    const scenario = scenarios[0];
    const trajectory = scenario.growth_trajectory;
    const targetWeight = parseFloat(formData.target_average_weight_g);
    const lastCp = validCheckpoints[validCheckpoints.length - 1];
    const lastCpDate = new Date(lastCp.date);

    // Find nearest trajectory index for last checkpoint
    let anchorIdx = -1;
    let anchorBestDiff = Infinity;
    trajectory.forEach((pt, idx) => {
      const ptDate = parseTrajectoryDate(pt.date);
      const diff = Math.abs(ptDate.getTime() - lastCpDate.getTime());
      if (diff < anchorBestDiff) { anchorBestDiff = diff; anchorIdx = idx; }
    });
    if (anchorIdx < 0) return null;

    // Build the revised trajectory:
    // - Before anchor: copy original unchanged
    // - From anchor onward: recompute with re-anchored weight
    const revised = [];
    let currentWeight = lastCp.weight;
    let cumulativeFeed = 0;
    let reachedTargetIdx = -1;

    // Sum cumulative feed up to anchor from original
    for (let i = 0; i < anchorIdx; i++) {
      revised.push({ ...trajectory[i] });
      cumulativeFeed += trajectory[i].daily_feed_kg;
    }

    // From anchor onward: project with re-anchored weight
    for (let i = anchorIdx; i < trajectory.length; i++) {
      const orig = trajectory[i];
      if (i > anchorIdx) {
        currentWeight = currentWeight * (1 + orig.daily_sgr_percent / 100);
      }

      const fishCount = orig.fish_count;
      const biomass = parseFloat((currentWeight * fishCount / 1000).toFixed(1));

      // Feed rate from original model (daily_feed / biomass ratio)
      const origFeedRate = orig.biomass_kg > 0 ? orig.daily_feed_kg / orig.biomass_kg : 0;
      const dailyFeed = parseFloat((biomass * origFeedRate).toFixed(2));
      cumulativeFeed += dailyFeed;

      const weightGain = (currentWeight - parseFloat(formData.initial_average_weight_g)) * fishCount / 1000;
      const cumulativeFCR = weightGain > 0 ? parseFloat((cumulativeFeed / weightGain).toFixed(2)) : 0;

      revised.push({
        ...orig,
        average_weight_g: parseFloat(currentWeight.toFixed(2)),
        biomass_kg: biomass,
        daily_feed_kg: dailyFeed,
        cumulative_feed_kg: parseFloat(cumulativeFeed.toFixed(1)),
        cumulative_fcr: cumulativeFCR,
      });

      // Check if target reached
      if (reachedTargetIdx === -1 && currentWeight >= targetWeight) {
        reachedTargetIdx = revised.length - 1;
      }
    }

    // Compute KPIs
    const targetPoint = reachedTargetIdx >= 0 ? revised[reachedTargetIdx] : revised[revised.length - 1];
    const daysToTarget = reachedTargetIdx >= 0 ? targetPoint.day : null;
    const totalFeed = reachedTargetIdx >= 0
      ? revised.slice(0, reachedTargetIdx + 1).reduce((sum, p) => sum + p.daily_feed_kg, 0)
      : cumulativeFeed;

    return {
      growth_trajectory: revised,
      days_to_target: daysToTarget,
      target_date: targetPoint.date,
      target_weight_reached: reachedTargetIdx >= 0,
      final_weight_g: parseFloat(currentWeight.toFixed(2)),
      total_feed_required_kg: parseFloat(totalFeed.toFixed(1)),
      final_biomass_kg: targetPoint.biomass_kg,
      survivors_at_target: targetPoint.fish_count,
      last_sample_weight: lastCp.weight,
      last_sample_date: lastCp.date,
    };
  }, [scenarios, validCheckpoints, formData.target_average_weight_g, formData.initial_average_weight_g]);

  // Table view: 'original' or 'sample-updated'
  const [tableView, setTableView] = useState('original');

  // Get trajectory data for the active table view
  const getTableTrajectory = () => {
    if (tableView === 'sample-updated' && sampleUpdatedScenario) {
      return sampleUpdatedScenario.growth_trajectory;
    }
    return scenarios[0]?.growth_trajectory || [];
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-[#0891b2] hover:text-[#0e7490]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center space-x-3">
            <TrendingUp className="h-8 w-8 text-[#0891b2]" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Growth Prognosis</h1>
              <p className="text-gray-600 mt-1">Predict future growth and feed requirements</p>
            </div>
          </div>
        </div>

        <ModuleExamplesBar
          examples={prognosisExamples}
          onSelect={loadExampleAndRun}
          moduleLabel="Growth Prognosis"
          isReadOnly={isReadOnly}
        />

        {/* Saved Prognoses - Quick Access Section */}
        {savedRecords.length > 0 && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg text-blue-800">Saved Prognoses</CardTitle>
                </div>
                <span className="text-sm text-blue-600">{savedRecords.length} saved</span>
              </div>
              <CardDescription className="text-blue-600">
                Click to load a previously saved prognosis
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {savedRecords.slice(0, 6).map((record) => (
                  <div
                    key={record.id}
                    onClick={() => handleLoadRecord(record)}
                    className="p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <InlineRename
                          value={record.name}
                          onSave={(newName) => renamePrognosisRecord(record.id, newName)}
                          className="font-medium text-gray-900 truncate group-hover:text-blue-600"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {record.species} • {record.initial_average_weight_g}g → {record.target_average_weight_g}g
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {record.scenarios?.length || 0} scenarios • {new Date(record.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecord(record.id, record.name);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 -mr-2 -mt-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {savedRecords.length > 6 && (
                <Button
                  variant="link"
                  onClick={() => setShowLoadDialog(true)}
                  className="mt-3 text-blue-600 hover:text-blue-800 p-0"
                >
                  View all {savedRecords.length} saved prognoses →
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Input Form */}
        <Card className="mb-8">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white">
            <CardTitle>Growth Prediction Parameters</CardTitle>
            <CardDescription className="text-gray-100">
              Enter initial conditions and target to calculate growth timeline and feed needs
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Read-Only Mode Banner */}
            {isReadOnly && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="read-only-banner">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Read-Only Mode</p>
                    <p className="text-xs text-amber-700">Your account is pending approval. You can explore the interface but cannot run analyses.</p>
                  </div>
                </div>
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-60' : ''}>
              {/* Species and Genetic Origin */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="species">Species <span className="text-red-600">*</span></Label>
                  <Select
                    value={formData.species}
                    onValueChange={(value) => handleFormChange('species', value)}
                    required
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                      <SelectValue placeholder="Select species" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Active Species</SelectLabel>
                        <SelectItem value="Seabream">Seabream (Sparus aurata)</SelectItem>
                        <SelectItem value="Seabass" data-testid="species-option-seabass">
                          Seabass (Dicentrarchus labrax)
                        </SelectItem>
                        <SelectItem value="Tilapia" data-testid="species-option-tilapia">
                          Tilapia (Oreochromis niloticus)
                        </SelectItem>
                        {userSpecies.filter(s => s.value === 'Tripletail').map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Coming Soon</SelectLabel>
                        {COMING_SOON_SPECIES.filter(s => !s.value.includes('Oreochromis')).map(species => (
                          <SelectItem 
                            key={species.value} 
                            value={species.value} 
                            disabled
                            className="text-gray-400 cursor-not-allowed"
                          >
                            {species.label} (Coming soon)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {/* Genetic Origin - Only show for Seabream */}
                {formData.species !== 'Tilapia' && formData.species !== 'Seabass' && formData.species !== 'Tripletail' && (
                  <div className="space-y-2">
                    <Label htmlFor="genetic_origin">Genetic Origin <span className="text-red-600">*</span></Label>
                    <Select
                      value={formData.genetic_origin}
                      onValueChange={(value) => handleFormChange('genetic_origin', value)}
                    >
                      <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Greek">Greek</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                        <SelectItem value="Italian">Italian</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* FCR Version selector — mirrors Spreadsheet Uploader / Snapshot /
                    Growth-vs-Model so users can choose a multiplier version that
                    matches their cage's historical FCR performance. Maps the
                    current (species, genetic_origin) → origin_code. */}
                {fcrOrigins.length > 0 && (() => {
                  const speciesLower = (formData.species || '').toLowerCase();
                  let code;
                  let label;
                  if (speciesLower.includes('tilapia')) { code = 'tilapia'; label = 'Tilapia'; }
                  else if (speciesLower.includes('seabass')) { code = 'seabass'; label = 'Seabass'; }
                  else if (speciesLower.includes('tripletail')) { code = 'tripletail'; label = 'Tripletail'; }
                  else { code = (formData.genetic_origin || 'greek').toLowerCase(); label = `Seabream · ${formData.genetic_origin || 'Greek'}`; }
                  const origin = fcrOrigins.find(o => (o.code || '').toLowerCase() === code);
                  const versions = origin?.versions || [];
                  if (versions.length === 0) {
                    return (
                      <div className="space-y-2">
                        <Label>FCR Version <span className="text-xs text-gray-500">({label})</span></Label>
                        <div
                          className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                          data-testid="prognosis-fcr-version-none"
                        >
                          No {label} FCR versions configured yet — using admin baseline (multiplier = 1.00×).
                        </div>
                        <FCRVersionGuidanceNote />
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="fcr_version_id">
                        FCR Version <span className="text-xs text-gray-500">({label})</span>
                      </Label>
                      <Select
                        value={formData.fcr_version_id || ''}
                        onValueChange={(value) => handleFormChange('fcr_version_id', value)}
                      >
                        <SelectTrigger
                          className="bg-emerald-50 border-emerald-300"
                          data-testid="prognosis-fcr-version-select"
                        >
                          <SelectValue placeholder="Select version" />
                        </SelectTrigger>
                        <SelectContent>
                          {versions.map(v => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.label || `${v.version_name} · ${v.multiplier_percent ?? 100}%`}
                              {origin?.default_version_id === v.id ? ' ★' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FCRVersionGuidanceNote />
                    </div>
                  );
                })()}
              </div>

              {/* Tilapia Static Model Banner (Phase B) — replaces the monthly
                  temperature grid for Tilapia. The tilapia_model.py curve is
                  time-driven and temperature-agnostic; use admin model % to
                  scale gain above the anchor. */}
              {formData.species === 'Tilapia' && (
                <div
                  className="bg-slate-50 text-slate-600 px-3 py-2.5 rounded-md border border-slate-200"
                  data-testid="tilapia-static-model-banner"
                >
                  <div className="flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-slate-700">
                        Static growth model — temperature-agnostic.
                      </p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        Tilapia uses a static time-driven curve derived from admin data (Day 1 → 219, 20g → 1,402.45g). To shift the curve, adjust the <strong className="font-medium text-slate-600">admin model %</strong> instead. Feed uses the weight-indexed BFCR curve. Missing-fish confidence is auto-clamped one level down.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Initial Conditions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="initial_average_weight_g">Initial Weight (g) <span className="text-red-600">*</span></Label>
                  <Input
                    id="initial_average_weight_g"
                    type="number"
                    step="0.1"
                    min={(formData.species || '').toLowerCase().includes('tilapia') ? '20' : '0.1'}
                    placeholder={(formData.species || '').toLowerCase().includes('tilapia') ? 'Min 20g' : 'e.g., 10.0'}
                    value={formData.initial_average_weight_g}
                    onChange={(e) => handleFormChange('initial_average_weight_g', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                  {(formData.species || '').toLowerCase().includes('tilapia') && (
                    <p className="text-xs text-emerald-700" data-testid="tilapia-min-weight-note">
                      For Tilapia: minimum average weight is <strong>20 g</strong>.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial_date">Initial Date <span className="text-red-600">*</span></Label>
                  <Input
                    id="initial_date"
                    type="date"
                    value={formData.initial_date}
                    onChange={(e) => handleFormChange('initial_date', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial_fish_number">Fish Count <span className="text-red-600">*</span></Label>
                  <Input
                    id="initial_fish_number"
                    type="number"
                    placeholder="e.g., 100000"
                    value={formData.initial_fish_number}
                    onChange={(e) => handleFormChange('initial_fish_number', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                </div>
              </div>

              {/* Temperature and Target */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Temperature Region - Show for SGR+temperature species
                    (Seabream, Seabass). Tilapia/Tripletail use static
                    time-driven curves and have no region dropdown. */}
                {formData.species !== 'Tilapia' && formData.species !== 'Tripletail' && (
                  <div className="space-y-2">
                    <Label htmlFor="temperature_region">Temperature Region <span className="text-red-600">*</span></Label>
                    <Select
                      value={formData.temperature_region}
                      onValueChange={(value) => handleFormChange('temperature_region', value)}
                      required
                    >
                      <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* TPS Installed Profiles */}
                        {temperatureRegions.filter(r => typeof r === 'string').length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100">
                              TPS Installed Profiles
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'string').map((region) => (
                              <SelectItem key={region} value={region}>
                                {region}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        
                        {/* My Custom Temperature Models */}
                        {temperatureRegions.filter(r => typeof r === 'object' && r.isCustom).length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-orange-600 bg-orange-50 mt-1">
                              My Temperature Models
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'object' && r.isCustom).map((model) => (
                              <SelectItem key={model.id} value={`custom:${model.id}`}>
                                {model.displayName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        
                        {/* Shared/Public Temperature Models */}
                        {temperatureRegions.filter(r => typeof r === 'object' && r.isPublic).length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-purple-600 bg-purple-50 mt-1">
                              Shared Models (Available to All)
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'object' && r.isPublic).map((model) => (
                              <SelectItem key={model.id} value={`custom:${model.id}`}>
                                {model.displayName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="target_average_weight_g">Target Weight (g) <span className="text-red-600">*</span></Label>
                  <Input
                    id="target_average_weight_g"
                    type="number"
                    step="0.1"
                    max={formData.species === 'Tilapia' ? '1400' : formData.species === 'Seabass' ? '600' : '800'}
                    placeholder={formData.species === 'Tilapia' ? 'e.g., 800.0' : formData.species === 'Seabass' ? 'e.g., 400.0' : 'e.g., 350.0'}
                    value={formData.target_average_weight_g}
                    onChange={(e) => handleFormChange('target_average_weight_g', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">
                    {formData.species === 'Tilapia'
                      ? 'Maximum: 1,400g — Tilapia static model horizon (day 219, 1,402g).'
                      : formData.species === 'Seabass'
                      ? 'Maximum: 600g — Seabass BFCR spreadsheet ceiling.'
                      : 'Maximum: 800g for Seabream'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cycle_mortality_percent">Cycle Mortality <span className="text-red-600">*</span></Label>
                  <Select
                    value={formData.cycle_mortality_percent}
                    onValueChange={(value) => handleFormChange('cycle_mortality_percent', value)}
                  >
                    <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                      <SelectItem value="25">25%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Model Adjustment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="model_adjustment_percent">Model Adjustment Factor (%)</Label>
                  <Input
                    id="model_adjustment_percent"
                    type="number"
                    step="1"
                    min="50"
                    max="150"
                    placeholder="100"
                    value={formData.model_adjustment_percent}
                    onChange={(e) => handleFormChange('model_adjustment_percent', e.target.value)}
                    className="bg-slate-50 border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">
                    Adjust model performance: 100% = baseline, 110% = 10% faster growth
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="daily_feed_adjustment_percent">Daily Feed Adjustment (%)</Label>
                  <Select
                    value={formData.daily_feed_adjustment_percent}
                    onValueChange={(value) => handleFormChange('daily_feed_adjustment_percent', value)}
                  >
                    <SelectTrigger className="bg-slate-50 border-[#0891b2]" data-testid="feed-adjustment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="55">55%</SelectItem>
                      <SelectItem value="60">60%</SelectItem>
                      <SelectItem value="65">65%</SelectItem>
                      <SelectItem value="70">70%</SelectItem>
                      <SelectItem value="75">75%</SelectItem>
                      <SelectItem value="80">80%</SelectItem>
                      <SelectItem value="85">85%</SelectItem>
                      <SelectItem value="90">90%</SelectItem>
                      <SelectItem value="95">95%</SelectItem>
                      <SelectItem value="100">100% (Default)</SelectItem>
                      <SelectItem value="105">105%</SelectItem>
                      <SelectItem value="110">110%</SelectItem>
                      <SelectItem value="115">115%</SelectItem>
                      <SelectItem value="120">120%</SelectItem>
                      <SelectItem value="125">125%</SelectItem>
                      <SelectItem value="130">130%</SelectItem>
                      <SelectItem value="135">135%</SelectItem>
                      <SelectItem value="140">140%</SelectItem>
                      <SelectItem value="145">145%</SelectItem>
                      <SelectItem value="150">150%</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Adjust feed amount (does NOT affect growth): 90% = 10% less feed, lower FCR
                  </p>
                </div>
              </div>

              {/* Model Source Selection */}
              <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <Label className="flex items-center space-x-2 mb-2">
                  <span>Model Source</span>
                  <span className="text-xs text-cyan-600 font-normal bg-cyan-100 px-2 py-0.5 rounded">New</span>
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    value={formData.model_source}
                    onValueChange={(value) => {
                      handleFormChange('model_source', value);
                      if (value === 'TPS') {
                        handleFormChange('custom_model_id', '');
                      }
                    }}
                    data-testid="model-source-select"
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select model source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TPS">TPS Standard Model (Default)</SelectItem>
                      <SelectItem value="CUSTOM" disabled={approvedModels.length === 0}>
                        My Approved Models {approvedModels.length === 0 && '(None available)'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {formData.model_source === 'CUSTOM' && (
                    <div>
                      {loadingModels ? (
                        <p className="text-xs text-gray-500 p-2">Loading models...</p>
                      ) : approvedModels.length === 0 ? (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          No approved models. Upload from Dashboard.
                        </p>
                      ) : (
                        <Select
                          value={formData.custom_model_id}
                          onValueChange={(value) => handleFormChange('custom_model_id', value)}
                          data-testid="custom-model-select"
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select your model" />
                          </SelectTrigger>
                          <SelectContent>
                            {approvedModels.map(model => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.model_name} ({model.species || 'Seabream'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
                {formData.model_source === 'TPS' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Using the standard TPS SGR model for growth calculations.
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || isReadOnly}
                className="w-full bg-[#0891b2] hover:bg-[#0e7490] text-white"
              >
                {isReadOnly ? <><Lock className="w-4 h-4 mr-2" />Pending Approval</> : loading ? 'Calculating...' : `Calculate Growth Prognosis${formatCostLabel('growth_prognosis')}`}
              </Button>
              <div className="flex justify-center pt-2">
                <UpdateCuratedExampleButton
                  module="growth_prognosis"
                  species={loadedCurated?.species}
                  currentSpecies={formData.species}
                  token={token}
                  isAdmin={!!user?.is_admin}
                  sourceRecordId={loadedCurated?.source_record_id}
                  currentRecordId={currentRecordId}
                  currentName={loadedCurated?.current_name}
                  buildRecordPatch={() => ({
                    species: formData.species,
                    genetic_origin: formData.genetic_origin || null,
                    // FCR version + model source must persist or the curated
                    // payload silently falls back to the species default and
                    // overrides the admin's edit.
                    fcr_version_id: formData.fcr_version_id || '',
                    model_source: formData.model_source || 'TPS',
                    custom_model_id: formData.custom_model_id || '',
                    initial_average_weight_g: parseFloat(formData.initial_average_weight_g) || 0,
                    initial_date: formData.initial_date,
                    temperature_region: formData.temperature_region || null,
                    target_average_weight_g: parseFloat(formData.target_average_weight_g) || 0,
                    initial_fish_number: parseInt(formData.initial_fish_number) || 0,
                    cycle_mortality_percent: parseFloat(formData.cycle_mortality_percent) || 0,
                  })}
                  onUpdated={() => refetchPrognosisExamples?.()}
                />
              </div>
              </fieldset>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {scenarios.length > 0 && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setShowTable(!showTable)}
                variant="outline"
                className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2]/10"
              >
                <Table className="mr-2 h-4 w-4" />
                {showTable ? 'Hide Daily Table' : 'Show Daily Table'}
              </Button>
              
              <Button
                onClick={handleExportExcel}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                {exporting ? 'Exporting...' : 'Download Excel'}
              </Button>

              <Button
                onClick={handleExportPdf}
                disabled={exportingPdf}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="export-pdf-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                {exportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>

              <Button
                onClick={() => {
                  if (scenarios.length === 0) {
                    toast.error('Calculate a prognosis first');
                    return;
                  }
                  setShowSaveDialog(true);
                }}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                data-testid="save-prognosis-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                Save Prognosis
              </Button>

              <Button
                onClick={() => {
                  fetchSavedRecords();
                  setShowLoadDialog(true);
                }}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                data-testid="load-prognosis-btn"
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                Load Saved
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setScenarios([]);
                  setResult(null);
                  setShowTable(false);
                  toast.success('All scenarios cleared');
                }}
                className="border-red-500 text-red-500 hover:bg-red-50"
              >
                Clear All Scenarios
              </Button>
            </div>

            {/* Forecast Status */}
            <Card className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Forecast Status</p>
                    <p className="text-lg font-bold mt-1">{scenarios[0]?.scenarioLabel || 'Calculated'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">
                      {1 + (validCheckpoints.length > 0 ? 1 : 0) + (showMissingFishIntelligence && missingFishIntelligence?.trajectoryJump?.jump_event_confirmed ? 1 : 0)}
                    </p>
                    <p className="text-xs opacity-75">Active forecast lines</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards - Show latest scenario */}
            {result && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Calendar className="h-8 w-8 mx-auto text-[#0891b2] mb-2" />
                      <p className="text-sm text-gray-600">Days to Target</p>
                      <p className="text-2xl font-bold text-gray-900">{result.days_to_target}</p>
                      <p className="text-xs text-gray-500 mt-1">{(result.days_to_target / 30).toFixed(1)} months</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <TrendingUp className="h-8 w-8 mx-auto text-green-600 mb-2" />
                      <p className="text-sm text-gray-600">Final Weight</p>
                      <p className="text-2xl font-bold text-gray-900">{result.target_average_weight_g}g</p>
                      <p className="text-xs text-gray-500 mt-1">From {result.initial_average_weight_g}g</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="h-8 w-8 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-orange-600 font-bold text-xs">Feed</span>
                      </div>
                      <p className="text-sm text-gray-600">Total Feed</p>
                      <p className="text-2xl font-bold text-gray-900">{result.total_feed_required_kg.toLocaleString()}kg</p>
                      {result.daily_feed_adjustment_percent && result.daily_feed_adjustment_percent !== 100 && (
                        <p className="text-xs text-orange-600 mt-1">Feed Adj: {result.daily_feed_adjustment_percent}%</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="h-8 w-8 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-blue-600 font-bold text-xs">Bio</span>
                      </div>
                      <p className="text-sm text-gray-600">Final Biomass</p>
                      <p className="text-2xl font-bold text-gray-900">{result.final_biomass_kg.toLocaleString()}kg</p>
                      <p className="text-xs text-gray-500 mt-1">{result.survivors_at_target.toLocaleString()} fish</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Sample-Updated KPI Cards — shown when samples provide a revised forecast */}
            {result && sampleUpdatedScenario && validCheckpoints.length > 0 && (
              <div data-testid="sample-updated-kpis">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-[#8b5cf6]" />
                  <p className="text-sm font-semibold text-purple-700">Sample-Updated Forecast</p>
                  <span className="text-xs text-purple-500">(anchored from last sample: {sampleUpdatedScenario.last_sample_weight}g on {new Date(sampleUpdatedScenario.last_sample_date).toLocaleDateString()})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-purple-200 bg-purple-50/40">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Calendar className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                        <p className="text-sm text-purple-700">Days to Target</p>
                        {sampleUpdatedScenario.target_weight_reached ? (
                          <>
                            <p className="text-2xl font-bold text-purple-900">{sampleUpdatedScenario.days_to_target}</p>
                            <p className="text-xs text-purple-500 mt-1">
                              {(sampleUpdatedScenario.days_to_target / 30).toFixed(1)} months
                              {sampleUpdatedScenario.days_to_target !== result.days_to_target && (
                                <span className={sampleUpdatedScenario.days_to_target < result.days_to_target ? ' text-green-600' : ' text-red-600'}>
                                  {' '}({sampleUpdatedScenario.days_to_target < result.days_to_target ? '' : '+'}{sampleUpdatedScenario.days_to_target - result.days_to_target}d)
                                </span>
                              )}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-2xl font-bold text-purple-900">{'>'}{result.days_to_target}</p>
                            <p className="text-xs text-orange-600 mt-1">Beyond original forecast</p>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-purple-200 bg-purple-50/40">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <TrendingUp className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                        <p className="text-sm text-purple-700">Projected Weight</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {sampleUpdatedScenario.target_weight_reached
                            ? `${result.target_average_weight_g}g`
                            : `${sampleUpdatedScenario.final_weight_g}g`}
                        </p>
                        <p className="text-xs text-purple-500 mt-1">
                          {sampleUpdatedScenario.target_weight_reached
                            ? `Reaches target on ${sampleUpdatedScenario.target_date}`
                            : `At end of forecast (target: ${result.target_average_weight_g}g)`}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-purple-200 bg-purple-50/40">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="h-8 w-8 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-purple-600 font-bold text-xs">Feed</span>
                        </div>
                        <p className="text-sm text-purple-700">Total Feed</p>
                        <p className="text-2xl font-bold text-purple-900">{sampleUpdatedScenario.total_feed_required_kg.toLocaleString()}kg</p>
                        {sampleUpdatedScenario.total_feed_required_kg !== result.total_feed_required_kg && (
                          <p className="text-xs mt-1">
                            <span className={sampleUpdatedScenario.total_feed_required_kg < result.total_feed_required_kg ? 'text-green-600' : 'text-red-600'}>
                              {sampleUpdatedScenario.total_feed_required_kg < result.total_feed_required_kg ? '' : '+'}
                              {(sampleUpdatedScenario.total_feed_required_kg - result.total_feed_required_kg).toLocaleString()}kg
                            </span>
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-purple-200 bg-purple-50/40">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="h-8 w-8 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-purple-600 font-bold text-xs">Bio</span>
                        </div>
                        <p className="text-sm text-purple-700">Final Biomass</p>
                        <p className="text-2xl font-bold text-purple-900">{sampleUpdatedScenario.final_biomass_kg.toLocaleString()}kg</p>
                        <p className="text-xs text-purple-500 mt-1">{sampleUpdatedScenario.survivors_at_target.toLocaleString()} fish</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Growth Forecast Chart */}
            <Card>
              <CardHeader>
                <CardTitle data-testid="growth-chart-title">Growth Forecast</CardTitle>
                <CardDescription>
                  {validCheckpoints.length > 0 && showMissingFishIntelligence && missingFishIntelligence?.trajectoryJump?.jump_event_confirmed
                    ? 'Showing Original, Sample-Updated, and Missing Fish Corrected forecasts'
                    : validCheckpoints.length > 0
                    ? 'Showing Original and Sample-Updated forecasts'
                    : 'Model growth prediction from initial to target weight'}
                </CardDescription>
              </CardHeader>
              <CardContent ref={pdfExportRef}>
                {scenarios.length === 0 ? (
                  <div className="h-96 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <TrendingUp className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p>Run a growth prognosis to see predictions here</p>
                      <p className="text-sm mt-2">Add sample checkpoints to see updated and corrected forecasts</p>
                    </div>
                  </div>
                ) : (
                  <div style={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        label={{ value: 'Date', position: 'insideBottom', offset: -5 }}
                        stroke="#6b7280"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(dateStr) => dateStr}
                      />
                      <YAxis 
                        label={{ value: 'Average Weight (g)', angle: -90, position: 'insideLeft' }}
                        stroke="#6b7280"
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #0891b2', borderRadius: '8px' }}
                        labelFormatter={(dateStr) => `Date: ${dateStr}`}
                        formatter={(value, name) => [value ? `${value.toFixed(1)}g` : 'N/A', name]}
                      />
                      <Legend />
                      {/* Line 1: Original Forecast (always) */}
                      <Line
                        type="monotone"
                        dataKey="Original Forecast"
                        stroke="#0891b2"
                        strokeWidth={2.5}
                        dot={false}
                        name="Original Forecast"
                      />
                      {/* Line 2: Sample-Updated Forecast (when samples entered) */}
                      {validCheckpoints.length > 0 && (
                        <Line
                          type="monotone"
                          dataKey="Sample-Updated Forecast"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          strokeDasharray="6 3"
                          dot={false}
                          connectNulls
                          name="Sample-Updated Forecast"
                        />
                      )}
                      {/* Line 3: Corrected Forecast (when missing fish confirmed) */}
                      {showMissingFishIntelligence && missingFishIntelligence?.trajectoryJump?.jump_event_confirmed && missingFishIntelligence?.trajectoryJump?.estimated_missing_fish > 0 && (
                        <Line
                          type="monotone"
                          dataKey="Corrected Forecast"
                          stroke="#ef4444"
                          strokeWidth={2}
                          strokeDasharray="8 4"
                          dot={false}
                          connectNulls
                          name="Corrected Forecast"
                        />
                      )}
                      {/* Sample checkpoint dots */}
                      {validCheckpoints.map((cp, idx) => {
                        const matchPoint = chartData.find(d => d.date === cp.dateFormatted);
                        if (!matchPoint) return null;
                        return (
                          <ReferenceDot
                            key={`sample-${idx}`}
                            x={cp.dateFormatted}
                            y={cp.weight}
                            r={5}
                            fill="#8b5cf6"
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sample Checkpoints & Missing Fish Analysis */}
            {scenarios.length > 0 && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="text-purple-800 flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Missing Fish Analysis
                  </CardTitle>
                  <CardDescription>
                    Enter actual sample weights to compare against model predictions and detect potential stock discrepancies
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Sample Checkpoints Input */}
                  <div className="space-y-4">
                    <Label className="text-sm font-medium text-gray-700">Sample Checkpoints</Label>
                    {sampleCheckpoints.map((checkpoint, index) => (
                      <div key={index} className="flex flex-wrap items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-purple-700">S{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <Input
                            type="date"
                            value={checkpoint.date}
                            onChange={(e) => handleSampleCheckpointChange(index, 'date', e.target.value)}
                            className="text-sm"
                            placeholder="Sample Date"
                          />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          <Input
                            type="number"
                            value={checkpoint.actual_weight_g}
                            onChange={(e) => handleSampleCheckpointChange(index, 'actual_weight_g', e.target.value)}
                            placeholder="Actual Weight (g)"
                            className="text-sm"
                            step="0.1"
                          />
                        </div>
                        {sampleCheckpoints.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSampleCheckpoint(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {sampleCheckpoints.length < 6 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addSampleCheckpoint}
                        className="text-purple-600 border-purple-300"
                      >
                        + Add Sample Checkpoint
                      </Button>
                    )}
                  </div>

                  {/* Run Analysis Button */}
                  <div className="flex justify-center">
                    <Button
                      onClick={runMissingFishIntelligenceAnalysis}
                      disabled={validCheckpoints.length === 0 || scenarios.length === 0}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="run-missing-fish-analysis-btn"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Run Missing Intelligence
                    </Button>
                  </div>

                  {/* Missing Fish Intelligence Results — Shared Component */}
                  {showMissingFishIntelligence && missingFishIntelligence && (
                    <div className="mt-6" data-testid="missing-fish-intelligence-results" ref={mfiPdfRef}>
                      <div className="mb-4 bg-sky-50 border border-sky-200 rounded-lg p-4" data-testid="fcr-unavailable-notice">
                        <p className="text-sm text-sky-800">
                          FCR analysis unavailable due to missing feed data. Upload to Spreadsheet Uploader to enable full FCR, biomass validation, and missing fish detection.
                        </p>
                      </div>
                      <MissingFishIntelligenceReport 
                        data={missingFishIntelligence}
                        economicImpact={missingFishIntelligence?.economicImpact}
                        dataQuality={missingFishIntelligence?.dataQuality}
                        mortalityCheck={missingFishIntelligence?.mortalityCheck}
                        samplingRecommendation={missingFishIntelligence?.samplingRecommendation}
                        overfeeding={missingFishIntelligence?.overfeedingRisk}
                        trajectoryJump={trajectoryJump}
                        fcrBasedDetection={missingFishIntelligence?.fcrBasedDetection}
                        growthFit={missingFishIntelligence?.growthFit}
                        methodResolution={missingFishIntelligence?.methodResolution}
                        sharedMetrics={missingFishIntelligence?.sharedMetrics}
                        ssot={missingFishIntelligence?.ssot}
                        showDeviationTable={true}
                        showExplanations={true}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Daily Data Table */}
            {showTable && scenarios.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Table className="h-5 w-5" />
                        Daily Growth Data
                      </CardTitle>
                      <CardDescription>
                        Detailed day-by-day growth metrics
                      </CardDescription>
                    </div>
                    
                    {/* View Selector: Original vs Sample-Updated */}
                    <div className="w-full sm:w-72">
                      <Select
                        value={tableView}
                        onValueChange={(value) => setTableView(value)}
                        data-testid="table-view-selector"
                      >
                        <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="original">Original Forecast</SelectItem>
                          {sampleUpdatedScenario && (
                            <SelectItem value="sample-updated">Sample-Updated Forecast</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {getTableTrajectory().length > 0 ? (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto relative">
                      {tableView === 'sample-updated' && (
                        <div className="mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded text-sm text-purple-700">
                          Showing sample-updated values — weights re-anchored from last sample ({sampleUpdatedScenario?.last_sample_weight}g). Feed and biomass are proportionally adjusted.
                        </div>
                      )}
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                          <tr className={tableView === 'sample-updated' ? 'bg-[#8b5cf6] text-white' : 'bg-[#0891b2] text-white'}>
                            <th className="px-3 py-2 text-left font-medium">Day</th>
                            <th className="px-3 py-2 text-left font-medium">Date</th>
                            <th className="px-3 py-2 text-right font-medium">Weight (g)</th>
                            <th className="px-3 py-2 text-right font-medium">Temp (°C)</th>
                            <th className="px-3 py-2 text-right font-medium">Daily SGR (%)</th>
                            <th className="px-3 py-2 text-right font-medium">Acc. SGR (%)</th>
                            <th className="px-3 py-2 text-right font-medium">Daily Mort. (%)</th>
                            <th className="px-3 py-2 text-right font-medium">Acc. Mort. (%)</th>
                            <th className="px-3 py-2 text-right font-medium">Fish in Cage</th>
                            <th className="px-3 py-2 text-right font-medium">Biomass (kg)</th>
                            <th className="px-3 py-2 text-right font-medium">Daily Feed (kg)</th>
                            <th className="px-3 py-2 text-right font-medium">Acc. Feed (kg)</th>
                            <th className="px-3 py-2 text-right font-medium">Acc. FCR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getTableTrajectory().map((point, idx) => (
                            <tr 
                              key={point.day} 
                              className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                            >
                              <td className="px-3 py-2 text-gray-700">{point.day}</td>
                              <td className="px-3 py-2 text-gray-700">{point.date}</td>
                              <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                {point.average_weight_g.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.temperature_c.toFixed(1)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.daily_sgr_percent.toFixed(3)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.accumulated_sgr_percent.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.daily_mortality_percent.toFixed(4)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.accumulated_mortality_percent.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right text-blue-600 font-medium">
                                {point.fish_count.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.biomass_kg.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.daily_feed_kg.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {point.cumulative_feed_kg.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                {point.cumulative_fcr.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-gray-500 mt-3 text-center">
                        Showing {getTableTrajectory().length} days of data
                        {tableView === 'sample-updated' && ' (Sample-Updated)'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      No trajectory data available
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Interpretation */}
            {result && (
              <Card>
                <CardHeader>
                  <CardTitle>Latest Scenario Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    {result.interpretation}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Active Forecast Lines Legend */}
            <Card>
              <CardHeader>
                <CardTitle>Active Forecast Lines</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Original Forecast — always present */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#0891b2' }} />
                      <span className="font-medium">Original Forecast</span>
                    </div>
                    <div className="text-sm text-gray-600 space-x-4">
                      <span>{scenarios[0]?.days_to_target} days</span>
                      <span>•</span>
                      <span>{scenarios[0]?.total_feed_required_kg?.toLocaleString()}kg feed</span>
                    </div>
                  </div>
                  {/* Sample-Updated Forecast */}
                  {validCheckpoints.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded border border-purple-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#8b5cf6' }} />
                        <span className="font-medium text-purple-800">Sample-Updated Forecast</span>
                      </div>
                      <span className="text-sm text-purple-600">{validCheckpoints.length} sample(s) applied</span>
                    </div>
                  )}
                  {/* Corrected Forecast */}
                  {showMissingFishIntelligence && missingFishIntelligence?.trajectoryJump?.jump_event_confirmed && missingFishIntelligence.trajectoryJump.estimated_missing_fish > 0 && (
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#ef4444' }} />
                        <span className="font-medium text-red-800">Corrected Forecast</span>
                      </div>
                      <span className="text-sm text-red-600">~{missingFishIntelligence.trajectoryJump.estimated_missing_fish?.toLocaleString()} fish loss detected</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Wingman Proactive Alert */}
        {result && <WingmanNudge patterns={detectPrognosisPatterns({ result, missingFishIntelligence })} />}

        {/* Wingman AI Insight */}
        {result && (
          <WingmanInsight
            analysisType="Growth Prognosis"
            analysisData={{
              species: result.species,
              initial_weight: result.initial_average_weight_g,
              target_weight: result.target_average_weight_g,
              days_to_target: result.days_to_target,
              total_feed_required_kg: result.total_feed_required_kg,
              final_biomass_kg: result.final_biomass_kg,
              survivors: result.survivors_at_target,
              mortality_rate: result.mortality_rate_percent,
              missing_fish_detected: !!(
                missingFishIntelligence?.trajectoryJump?.jump_event_confirmed ||
                missingFishIntelligence?.ssot?.hasMissingFish ||
                missingFishIntelligence?.growthFit?.detected
              ),
              missing_fish_count: missingFishIntelligence?.ssot?.missingFishCount || missingFishIntelligence?.growthFit?.estimatedMissing || 0,
              growth_fit: missingFishIntelligence?.growthFit ? {
                detected: !!missingFishIntelligence.growthFit.detected,
                estimatedMissing: missingFishIntelligence.growthFit.estimatedMissing || 0,
                rSquared: missingFishIntelligence.growthFit.rSquared,
              } : null,
              actionability: missingFishIntelligence?.ssot?.actionability || missingFishIntelligence?.decisionContext?.decisionClass || null,
              detection_strength: missingFishIntelligence?.ssot?.detectionStrength || null,
              ssot: missingFishIntelligence?.ssot ? {
                hasMissingFish: !!missingFishIntelligence.ssot.hasMissingFish,
                missingFishCount: missingFishIntelligence.ssot.missingFishCount || 0,
                actionability: missingFishIntelligence.ssot.actionability,
                detectionStrength: missingFishIntelligence.ssot.detectionStrength,
                growthFitHasDeviation: !!(missingFishIntelligence.growthFit?.detected || missingFishIntelligence.growthFit?.estimatedMissing > 0),
              } : null,
            }}
          />
        )}

        {/* Admin-only: Save / Discard report-visibility changes */}
        <AdminHiddenSectionsOverview className="mb-4" />

        {/* Help & Benefits Section */}
        <HelpBenefits 
          youtubeUrl={HELP_URLS.growthPrognosis.youtube}
          benefitsUrl={HELP_URLS.growthPrognosis.benefits}
          featureName="Growth Prognosis"
        />

        {/* Glossary footer (collapsible) — only when a result is on screen */}
        {result && <ReportGlossary />}
        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Save Prognosis</DialogTitle>
              <DialogDescription>
                Save this prognosis calculation for future reference.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="recordName">Prognosis Name</Label>
                <Input
                  id="recordName"
                  value={recordName}
                  onChange={(e) => setRecordName(e.target.value)}
                  placeholder="e.g., Batch A - Summer 2024"
                />
              </div>
              <div className="text-sm text-gray-500">
                <p><strong>Species:</strong> {formData.species}{formData.species === 'Seabream' && formData.genetic_origin ? ` (${formData.genetic_origin})` : ''}</p>
                <p><strong>Target:</strong> {formData.initial_average_weight_g}g → {formData.target_average_weight_g}g</p>
                <p><strong>Fish Count:</strong> {formData.initial_fish_number}</p>
                <p><strong>Scenarios:</strong> {scenarios.length}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRecord} disabled={savingRecord}>
                {savingRecord ? 'Saving...' : (currentRecordId ? 'Update' : 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Load Dialog */}
        <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Load Saved Prognosis</DialogTitle>
              <DialogDescription>
                Select a previously saved prognosis to load.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {loadingRecords ? (
                <div className="text-center py-8 text-gray-500">Loading records...</div>
              ) : savedRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No saved prognoses found. Calculate and save a prognosis first.
                </div>
              ) : (
                <>
                  {prognosisSortFilterBar}
                  {filteredPrognosisRecords.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 cursor-pointer" onClick={() => handleLoadRecord(record)}>
                      <InlineRename
                        value={record.name}
                        onSave={(newName) => renamePrognosisRecord(record.id, newName)}
                        className="font-medium"
                      />
                      <p className="text-sm text-gray-500">
                        {record.species}{record.species === 'Seabream' && record.genetic_origin ? ` (${record.genetic_origin})` : ''} • {record.initial_average_weight_g}g → {record.target_average_weight_g}g • {record.scenarios?.length || 0} scenarios
                      </p>
                      <p className="text-xs text-gray-400">
                        Last updated: {new Date(record.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecord(record.id, record.name);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLoadDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
